import { type ForwardedRef, forwardRef } from 'react';
import styled from 'styled-components';
import type { IBreakpoint, IResponsiveImageModel } from 'shared/interfaces/responsiveImage';

const StyledImg = styled.img`
    max-width: 100%;
    height: auto;
    width: auto;
`;

function createMediaCondition(breakPoint: IBreakpoint): string {
    const { minWidth, maxWidth } = breakPoint;
    const hasMinWidth = !!minWidth;
    const hasMaxWidth = !!maxWidth;

    const minWidthQuery = `${hasMinWidth ? `(min-width: ${minWidth}px)` : ''}`;
    const maxWidthQuery = `${hasMaxWidth ? `(max-width: ${maxWidth}px)` : ''}`;
    const separator = `${hasMinWidth && hasMaxWidth ? 'and' : ''}`;

    return `${minWidthQuery} ${separator} ${maxWidthQuery}`.trim();
}

/**
 * @deprecated Use DynamicImageRenderer instead
 */
const PictureDeprecated = forwardRef(
    (props: IResponsiveImageModel, ref: ForwardedRef<HTMLPictureElement>): JSX.Element | null => {
        const { sources, fallBackUrl, altText, lazyLoad, ...restProps } = props;
        if (!fallBackUrl && !sources?.length) {
            return null;
        }

        return (
            <picture ref={ref} {...restProps}>
                {!!sources?.length &&
                    sources.map((source, index): JSX.Element | null => {
                        if (!source.images) return null;
                        const { images, breakPoint } = source;
                        const { width, height } = images[0] || {};
                        const srcsetString = images.reduce(
                            (acc, image): string =>
                                `${acc.length ? `${acc}, ` : acc}${image.url} ${image.width ? `${image.width}w` : ''}`,
                            ''
                        );

                        return (
                            <source
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                media={breakPoint && createMediaCondition(breakPoint)}
                                srcSet={srcsetString}
                                width={width}
                                height={height}
                            />
                        );
                    })}

                <StyledImg
                    className="img"
                    src={fallBackUrl}
                    alt={altText || ''}
                    loading={lazyLoad ? 'lazy' : 'eager'}
                />
            </picture>
        );
    }
);

export default PictureDeprecated;
